// Escolha de hunt: o "mapa de expedição". Cabeçalho com bússola, lista de hunts em cards à esquerda (busca, filtro
// "Do meu level"/"Todas", faixa de dificuldade pelo seu level) e a hunt escolhida à direita (monstros, loots raros,
// andares e as boxes que o personagem vai farmar). Ao entrar, a janela da venda automática.
import { useEffect, useMemo, useState } from 'preact/hooks';
import { api } from '../../api';
import { FLOOR_UNLOCK_KILLS, floorUnlocked, type HuntInfo } from '../../../../shared/src/game/world';
import { catalogNow } from '../items';
import { FOOD_SECONDS } from '../../../../shared/src/game/helper';
import { ItemImg } from './ItemSlot';
import { Portrait } from './BattleList';

const THEME: Record<string, string> = {
  cave: 'Caverna', earth: 'Terra', desert: 'Deserto', ice: 'Gelo', lava: 'Lava', jungle: 'Selva', forest: 'Floresta',
  tomb: 'Cripta', ocean: 'Submerso', crystal: 'Cristal', honeycomb: 'Ninho', mold: 'Fungos', swamp: 'Pântano',
};
const THEME_ICON: Record<string, string> = {
  cave: '⛰️', earth: '🪨', desert: '🏜️', ice: '❄️', lava: '🌋', jungle: '🌴', forest: '🌲',
  tomb: '⚰️', ocean: '🌊', crystal: '💎', honeycomb: '🐝', mold: '🍄', swamp: '🐸',
};
type Hunt = HuntInfo & { online: number; event?: 'active' | 'soon' };
const fmt = (n: number) => n.toLocaleString('pt-BR');
const HUNT_GAP = 20;

/** Faixa da hunt em relação ao seu level (o level da hunt é só recomendação). */
type Band = 'easy' | 'ideal' | 'hard';
const band = (huntLevel: number, level: number): Band => (huntLevel > level ? 'hard' : level - huntLevel <= 15 ? 'ideal' : 'easy');
const BAND_NAME: Record<Band, string> = { easy: 'Fácil', ideal: 'Ideal', hard: 'Perigosa' };
const BAND_TIP: Record<Band, string> = {
  easy: 'Bem abaixo do seu level: segura, mas rende menos.',
  ideal: 'Na medida para o seu level.',
  hard: 'Acima do seu level: mais XP, mais risco. Cuidado!',
};

export function Hunts(props: {
  level: number; current: string | null; progress: Record<string, number[]>;
  /** quantos monstros juntar antes de lutar (0/1 = um de cada vez) */
  lure: number; onLure: (n: number) => void;
  onEnter: (id: string, floor: number) => void; onTemple: () => void;
  keepItems?: number[]; autoSell?: boolean; onSellPrefs?: (keep: number[], autoSell: boolean) => void;
  /** abrir já com esta hunt escolhida (botão "ir para a hunt" das tarefas) */
  initial?: string;
}) {
  // ao entrar numa hunt: janela da venda automática com todos os drops possíveis dela
  const [ask, setAsk] = useState<{ h: Hunt; floor: number } | null>(null);
  const enter = (id: string, floor: number) => {
    const h = list?.find((x) => x.id === id);
    if (h && props.onSellPrefs && props.current !== 'hunt-' + id) setAsk({ h, floor }); else props.onEnter(id, floor);
  };
  const [list, setList] = useState<Hunt[] | null>(null);
  const [err, setErr] = useState('');
  const [filter, setFilter] = useState<'mine' | 'all'>(props.initial ? 'all' : 'mine');
  const [q, setQ] = useState('');
  const [sel, setSel] = useState<string | null>(props.initial ?? null);
  const [floor, setFloor] = useState(0);
  const [help, setHelp] = useState(false);
  useEffect(() => { api.hunts().then(setList).catch((e) => setErr(e.message)); }, []);
  // aberta pelo botão das tarefas: mostra a hunt escolhida na lista
  useEffect(() => { if (list && props.initial) setTimeout(() => (document.querySelector('.hu-card.on') as HTMLElement | null)?.scrollIntoView({ block: 'nearest' }), 50); }, [list]);

  const shown = useMemo(() => {
    if (!list) return [];
    const t = q.trim().toLowerCase();
    // "Do meu level": hunts de 20 levels abaixo até 20 levels acima (decisão do Arthur, 14/10)
    return list.filter((h) => (filter === 'all' || Math.abs(h.level - props.level) <= HUNT_GAP)
      && (!t || h.name.toLowerCase().includes(t) || h.monsters.some((m) => m.toLowerCase().includes(t))))
      .sort((a, b) => a.level - b.level);
  }, [list, filter, q, props.level]);

  if (err) return <p class="error">{err}</p>;
  if (!list) return <p class="muted">Carregando hunts...</p>;
  // escolhida: a clicada, senão a da hunt atual, senão a mais forte do meu level
  const cur = list.find((h) => h.id === sel) ?? list.find((h) => 'hunt-' + h.id === props.current) ?? [...shown].reverse().find((h) => h.level <= props.level) ?? shown[0] ?? list[0]!;
  const mine = list.filter((h) => Math.abs(h.level - props.level) <= HUNT_GAP).length;
  const online = list.reduce((t, h) => t + (h.online || 0), 0);
  const here = list.find((h) => 'hunt-' + h.id === props.current);
  const boostC = list.find((h) => h.boostCreature);
  const boostB = list.find((h) => h.boostBoss);
  const evt = list.find((h) => h.event);
  // nunca caçou: sem progresso em nenhuma hunt e não está numa hunt agora
  const fresh = !props.current?.startsWith('hunt-') && !Object.values(props.progress ?? {}).some((p) => p?.some((k) => k > 0));
  const pick = (id: string) => { setSel(id); setFloor(0); };
  const away = props.current?.startsWith('hunt-') || props.current?.startsWith('quest-') || props.current?.startsWith('cq-') || props.current?.startsWith('boss-') || props.current === 'rift' || props.current === 'training' || props.current === 'tower' || props.current === 'wild' || props.current?.startsWith('labyrinth-');

  return (
    <div class="hu">
      {/* cabeçalho: a mesa do cartógrafo */}
      <div class="hu-head">
        <div class="hu-compass" aria-hidden="true"><i class="hu-needle" /><b>N</b></div>
        <div class="hu-head-txt">
          <div class="hu-title">Mapa de Caças <button class="hu-help" onClick={() => setHelp(!help)} title="Como funciona">?</button></div>
          <div class="hu-sub">Escolha a caverna, o andar e as boxes. O personagem caça sozinho até você mandar voltar.</div>
          <div class="hu-stats">
            <span title="Hunts entre 20 levels abaixo e 20 acima do seu"><b>{mine}</b> no seu level</span>
            <span title="Jogadores caçando em todas as hunts agora"><b class="hu-live">{fmt(online)}</b> caçando agora</span>
            <span title="Seu level"><b>Lv {props.level}</b></span>
          </div>
        </div>
        <div class={'hu-where' + (here ? ' on' : '')}>
          <small>Você está em</small>
          <b>{here ? here.name : away ? 'Outra área' : 'Noxara'}</b>
          {away && <button class="tb-button" onClick={props.onTemple}>Voltar para Noxara</button>}
        </div>
      </div>

      {(help || fresh) && (
        <div class="hu-steps">
          <div><i>🧭</i><b>1. Escolha a hunt</b><small>A cor do level mostra se ela é fácil, ideal ou perigosa para você. O level é só recomendação.</small></div>
          <div><i>🪜</i><b>2. Escolha o andar</b><small>Andares mais fundos têm boxes maiores. Libere com {fmt(FLOOR_UNLOCK_KILLS)} abates no andar anterior.</small></div>
          <div><i>📦</i><b>3. Marque as boxes</b><small>O personagem só ataca nas boxes marcadas; nas outras ele só passa pelo corredor.</small></div>
          <div><i>💰</i><b>4. Entre e deixe caçar</b><small>Ao entrar você escolhe o que a venda automática vende. O gold vai para o banco.</small></div>
        </div>
      )}

      {/* destaques do dia: criatura/boss do dia e evento */}
      {(boostC || boostB || evt) && (
        <div class="hu-daily">
          {boostC && <button class="hu-daily-chip" onClick={() => pick(boostC.id)} title="+100% XP e loot dobrado nesta criatura">
            <span class="hu-star">★</span><span><small>Criatura do dia</small><b>{boostC.boostCreature}</b><em>{boostC.name}</em></span></button>}
          {boostB && <button class="hu-daily-chip boss" onClick={() => pick(boostB.id)} title="Drop dobrado no boss desta hunt">
            <span class="hu-star">♛</span><span><small>Boss do dia</small><b>{boostB.boostBoss}</b><em>{boostB.name}</em></span></button>}
          {evt && <button class="hu-daily-chip evt" onClick={() => pick(evt.id)} title="Hunt da semana: XP em dobro no sábado e no domingo">
            <span class="hu-star">✦</span><span><small>{evt.event === 'active' ? 'Evento: XP em dobro!' : 'Evento do fim de semana'}</small><b>{evt.name}</b><em>Level {evt.level}+</em></span></button>}
        </div>
      )}

      <div class="hu-body">
        <div class="hu-side">
          <div class="hu-tabs">
            <button class={'tb-button' + (filter === 'mine' ? ' green' : '')} onClick={() => setFilter('mine')}>Do meu level (±20)</button>
            <button class={'tb-button' + (filter === 'all' ? ' green' : '')} onClick={() => setFilter('all')}>Todas ({list.length})</button>
          </div>
          <input class="hu-search" value={q} placeholder="🔍 Buscar hunt ou monstro" onInput={(e) => setQ((e.target as HTMLInputElement).value)} />
          <ul class="hu-list">
            {shown.map((h) => {
              const b = band(h.level, props.level), isHere = 'hunt-' + h.id === props.current;
              return (
                <li key={h.id} class={'hu-card hu-' + b + (h.id === cur.id ? ' on' : '') + (isHere ? ' here' : '')} onClick={() => pick(h.id)} title={BAND_TIP[b]}>
                  <span class="hu-faces">{(h.details ?? []).slice(0, 3).map((d) => <Portrait key={d.name} outfit={d.outfit} size={32} title={d.name} />)}</span>
                  <span class="hu-card-txt">
                    <b>{h.name}</b>
                    <small>{THEME_ICON[h.theme] ?? '🗺️'} {THEME[h.theme] ?? h.theme}{h.online ? <> · <span class="hu-live">{h.online} caçando</span></> : null}</small>
                    <span class="hu-marks">
                      {isHere && <em class="hu-mark here">você está aqui</em>}
                      {h.event && <em class="hu-mark evt">{h.event === 'active' ? '2x XP' : 'evento'}</em>}
                      {(h.boostCreature || h.boostBoss) && <em class="hu-mark star" title={[h.boostCreature && `Criatura do dia: ${h.boostCreature}`, h.boostBoss && `Boss do dia: ${h.boostBoss}`].filter(Boolean).join(' · ')}>★ do dia</em>}
                    </span>
                  </span>
                  <span class="hu-lvl"><b>{h.level}+</b><small>{BAND_NAME[b]}</small></span>
                </li>
              );
            })}
            {!shown.length && <li class="hu-empty muted">Nenhuma hunt encontrada.{filter === 'mine' && <> Tente <a onClick={() => setFilter('all')}>Todas</a>.</>}</li>}
          </ul>
        </div>
        <HuntDetail h={cur} level={props.level} floor={Math.min(floor, cur.floors - 1)} setFloor={setFloor}
          progress={props.progress[cur.id]} here={props.current === 'hunt-' + cur.id}
          lure={props.lure} onLure={props.onLure} onEnter={enter} />
      </div>
      {ask && <AutoSellAsk h={ask.h} keep={props.keepItems ?? []} autoSell={props.autoSell !== false} onCancel={() => setAsk(null)}
        onDone={(keep, on) => { props.onSellPrefs!(keep, on); setAsk(null); props.onEnter(ask.h.id, ask.floor); }} />}
    </div>
  );
}

function HuntDetail(props: {
  h: Hunt; level: number; floor: number; setFloor: (f: number) => void; progress?: number[]; here: boolean;
  lure: number; onLure: (n: number) => void; onEnter: (id: string, floor: number) => void;
}) {
  const { h, floor } = props;
  // sem limite de level (decisão do Arthur, 14/10): o level da hunt é só a recomendação
  const hard = props.level < h.level;
  const b = band(h.level, props.level);
  const cat = catalogNow();
  const open = floorUnlocked(props.progress, floor);
  const kills = props.progress?.[floor - 1] ?? 0;
  const [lureTxt, setLureTxt] = useState(String(props.lure));
  return (
    <div class={'hu-detail hu-' + b}>
      <div class="hu-dhead">
        <div class="hu-dtitle">
          <b>{h.name}</b>
          <div class="hu-tags">
            <span class="hu-tag">{THEME_ICON[h.theme] ?? '🗺️'} {THEME[h.theme] ?? h.theme}</span>
            <span class={'hu-tag lvl hu-' + b} title={BAND_TIP[b]}>Level {h.level}+ · {BAND_NAME[b]}</span>
            <span class="hu-tag"><span class="hu-live">● {h.online}</span> caçando agora</span>
            {props.here && <span class="hu-tag here">Você está aqui</span>}
          </div>
        </div>
        {(h.event || h.boostCreature || h.boostBoss) && (
          <div class="hu-banners">
            {h.event && <span class="hu-banner evt">{h.event === 'active' ? '✦ EVENTO: XP em dobro!' : '✦ Evento do fim de semana'}</span>}
            {h.boostCreature && <span class="hu-banner">★ Criatura do dia: <b>{h.boostCreature}</b> (+100% XP, loot dobrado)</span>}
            {h.boostBoss && <span class="hu-banner">♛ Boss do dia: <b>{h.boostBoss}</b> (drop dobrado)</span>}
          </div>
        )}
      </div>

      <div class="hu-sec">Monstros e loots mais raros</div>
      <ul class="hu-mons">
        {(h.details ?? []).map((d) => (
          <li key={d.name} class={d.name === h.boostCreature || d.name === h.boostBoss ? 'boost' : ''}>
            <span class="hu-face"><Portrait outfit={d.outfit} size={48} title={d.name} /></span>
            <span class="hu-mon">
              <b>{d.name}{(d.name === h.boostCreature || d.name === h.boostBoss) && <span class="hu-star"> ★</span>}</b>
              <small><span class="hu-hp">♥ {fmt(d.hp)}</span> <span class="hu-xp">✦ {fmt(d.exp)} XP</span></small>
              <span class="hu-loot">
                {d.rare.map((id) => <span key={id} class="item-slot small" title={cat?.get(id)?.name ?? ''}><ItemImg typeId={id} count={1} /></span>)}
                {!d.rare.length && <small class="muted">sem loot raro</small>}
              </span>
            </span>
          </li>
        ))}
        {!(h.details ?? []).length && <li class="muted small">{h.monsters.join(', ')}</li>}
      </ul>

      <div class="hu-sec">Andar</div>
      <div class="hu-floors">
        {Array.from({ length: h.floors }, (_, f) => {
          const ok = floorUnlocked(props.progress, f);
          const prev = props.progress?.[f - 1] ?? 0;
          return (
            <button key={f} class={'hu-floor' + (f === floor ? ' on' : '') + (ok ? '' : ' locked')} onClick={() => props.setFloor(f)}
              title={ok ? '' : `Mate ${FLOOR_UNLOCK_KILLS} monstros no andar ${f} para liberar (${prev}/${FLOOR_UNLOCK_KILLS})`}>
              <b>{ok ? '' : '🔒 '}Andar {f + 1}</b>
              <small>{ok ? (f === 0 ? 'Entrada' : 'Liberado') : `${fmt(Math.min(prev, FLOOR_UNLOCK_KILLS))}/${fmt(FLOOR_UNLOCK_KILLS)}`}</small>
              {f > 0 && <i class="hu-fbar"><i style={{ width: Math.min(100, (prev / FLOOR_UNLOCK_KILLS) * 100) + '%' }} /></i>}
            </button>
          );
        })}
      </div>

      <div class="hu-sec">Quantos monstros lurar</div>
      <p class="hu-note">Digite quantos monstros o personagem junta atrás de si antes de parar e lutar (fazer a box). Com 0 ou 1,
        ele ataca um de cada vez. Vale para todas as hunts e também aparece no Auto Helper.</p>
      <div class="hu-lure">
        <input type="number" inputMode="numeric" min={0} max={30} value={lureTxt}
          onInput={(e) => setLureTxt((e.target as HTMLInputElement).value)}
          onBlur={() => { const n = Math.max(0, Math.min(30, Math.trunc(Number(lureTxt) || 0))); setLureTxt(String(n)); props.onLure(n); }}
          onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }} />
        <span>{Number(lureTxt) >= 2 ? <>monstros por box</> : <>um monstro de cada vez</>}</span>
      </div>

      <div class="hu-go">
        <div class="hu-go-txt">
          {hard && <span class="hu-warn">⚠ Recomendada para level {h.level}+. Cuidado!</span>}
          {!open && <span class="hu-lock">🔒 Andar {floor + 1} bloqueado: {fmt(kills)}/{fmt(FLOOR_UNLOCK_KILLS)} abates no andar {floor}.</span>}
          {open && !hard && <span class="muted small">{props.here ? 'Você já está nesta hunt.' : 'Tudo pronto para a caçada.'}</span>}
        </div>
        <button class="tb-button green hu-enter" disabled={!open} onClick={() => props.onEnter(h.id, floor)}>
          {props.here ? `Ir para o andar ${floor + 1}` : `Entrar no andar ${floor + 1}`}
        </button>
      </div>
    </div>
  );
}

/** Janela ao entrar na hunt: o jogador escolhe o que a venda automática vende (marcado = vende). */
function AutoSellAsk(props: { h: Hunt; keep: number[]; autoSell: boolean; onCancel: () => void; onDone: (keep: number[], autoSell: boolean) => void }) {
  const cat = catalogNow();
  const ids = useMemo(() => [...new Set((props.h.details ?? []).flatMap((d) => d.loot ?? []))]
    .filter((id) => { const t = cat?.get(id); return t && !/ potion$| rune$/i.test(t.name) && !t.container && !FOOD_SECONDS[t.name.toLowerCase()]; })
    .sort((a, b) => (cat?.get(a)?.name ?? '').localeCompare(cat?.get(b)?.name ?? '')), [props.h.id]);
  const [keep, setKeep] = useState(() => new Set(props.keep.filter((id) => ids.includes(id))));
  const [on, setOn] = useState(props.autoSell);
  const toggle = (id: number) => { const n = new Set(keep); if (n.has(id)) n.delete(id); else n.add(id); setKeep(n); };
  // a lista de guardar é uma só para todas as hunts: troca só os itens desta
  const finish = (k: Set<number>, autoSell: boolean) => props.onDone([...props.keep.filter((id) => !ids.includes(id)), ...k], autoSell);
  return (
    <div class="autosell-bg" onClick={props.onCancel}>
      <div class="autosell hu-sell" onClick={(e) => e.stopPropagation()}>
        <div class="hu-sell-head"><span class="hu-sell-ico">💰</span><div><h4>Venda automática</h4><small>{props.h.name}</small></div>
          <span class="hu-sell-count"><b>{ids.length - keep.size}</b>/{ids.length}<small>serão vendidos</small></span></div>
        <p class="small muted">A cada 20 minutos (VIP: 10) o personagem vende sozinho o loot comum e o gold vai para o banco.
          Itens raros, épicos e lendários e a comida nunca são vendidos. <b>Marcado = vende</b>; desmarque o que quer guardar.</p>
        <label class={"row small hu-sell-toggle" + (on ? " on" : "")}><input type="checkbox" checked={on} onChange={() => setOn(!on)} /> Venda automática ligada</label>
        <div class="row small">
          <button class="tb-button" onClick={() => setKeep(new Set())}>Marcar todos</button>
          <button class="tb-button" onClick={() => setKeep(new Set(ids))}>Desmarcar todos</button>
          <span class="muted">{ids.length - keep.size} de {ids.length} serão vendidos</span>
        </div>
        <ul class="autosell-grid hu-sell-grid">
          {ids.map((id) => (
            <li key={id} class={keep.has(id) ? 'keep' : 'sell'} onClick={() => toggle(id)} title={keep.has(id) ? 'Guardar (não vende)' : 'Vende'}>
              <input type="checkbox" checked={!keep.has(id)} readOnly />
              <span class="item-slot small"><ItemImg typeId={id} count={1} /></span>
              <span class="autosell-name">{cat?.get(id)?.name ?? id}</span>
            </li>
          ))}
          {!ids.length && <li class="muted">Esta hunt não tem loot para vender.</li>}
        </ul>
        <div class="row between">
          <button class="tb-button" onClick={props.onCancel}>Cancelar</button>
          <div class="row">
            <button class="tb-button" onClick={() => finish(new Set(), true)}>Vender tudo e entrar</button>
            <button class="tb-button green" onClick={() => finish(keep, on)}>Entrar</button>
          </div>
        </div>
      </div>
    </div>
  );
}
